import { useContext } from 'react';
import { TenantContext } from '../../../context/tenant-context';
import {
  ItemTaxCategory,
  SearchTaxCodesRequest,
  SearchTaxJurisdictionsRequest,
  TaxCode,
  TaxCategory,
  TaxJurisdiction,
  TaxLocationLink,
} from './tax';
import { handlePaginatedRequest, handleRequest } from '../quipli-service-client';
import { TaxCodeParsers } from './tax-parsers';
import toTax = TaxCodeParsers.toTaxCode;
import toTaxJurisdictionRow = TaxCodeParsers.toTaxJurisdictionRow;
import { GenericSearchRequest } from '../../../util/util';
import { OrderType } from '../order/order';
import ItemTaxCategoryJson = TaxCodeParsers.ItemTaxCategoryJson;
import { ItemType } from '../accounting/accounting';
import { Pagination } from '../pagination/pagination';

export const useTaxApiV1 = () => {
  const { tenant, contextPath } = useContext(TenantContext);

  async function createTaxJurisdiction(jurisdiction: {
    name: string;
    percentage: number;
    generalLedgerAccountId?: string;
  }) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/tax/taxJurisdiction`,
      data: {
        jurisdiction,
      },
      headers: { 'X-Tenant': tenant },
    };

    return await handleRequest<TaxJurisdiction>(config);
  }

  async function updateTaxJurisdiction(jurisdiction: {
    id: string;
    name: string;
    percentage: number;
    generalLedgerAccountId?: string;
    linkId?: string;
    taxCategories?: TaxCategory[];
  }) {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/tax/taxJurisdiction`,
      data: {
        jurisdiction,
      },
      headers: { 'X-Tenant': tenant },
    };

    return await handleRequest<TaxJurisdiction>(config);
  }

  async function createTaxCode(taxCode: {
    name: string;
    jurisdictionIds: string[];
    qboTaxCodeId?: string;
    source: string;
  }) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/tax/taxCode`,
      data: {
        taxCode: taxCode,
      },
      headers: { 'X-Tenant': tenant },
    };

    return toTax(await handleRequest<TaxCodeParsers.TaxCodeJson>(config));
  }

  // Only needs to update the QBO Tax code id for now
  async function updateTaxCode({
    id,
    name,
    jurisdictionIds,
    qboTaxCodeId,
  }: {
    id: string;
    name?: string;
    jurisdictionIds?: string[];
    qboTaxCodeId?: string;
  }) {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/tax/taxCode`,
      data: {
        taxCode: { id, jurisdictionIds, qboTaxCodeId, name },
      },
      headers: { 'X-Tenant': tenant },
    };

    return toTax(await handleRequest<TaxCodeParsers.TaxCodeJson>(config));
  }

  async function searchTaxJurisdictions(request: SearchTaxJurisdictionsRequest) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/tax/taxJurisdictions/search`,
      data: request,
      headers: { 'X-Tenant': tenant },
    };

    const result = await handleRequest<TaxCodeParsers.TaxJurisdictionRowJson[]>(config);
    return result.map(TaxCodeParsers.toTaxJurisdictionRow);
  }

  async function searchTaxCodes(request: SearchTaxCodesRequest): Promise<Pagination<TaxCode>> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/tax/taxCodes/search`,
      headers: { 'X-Tenant': tenant },
      data: request,
    };
    const { items, pagination } = await handlePaginatedRequest<TaxCodeParsers.TaxCodeJson>(config);
    return { items: items.map(TaxCodeParsers.toTaxCode), pagination };
  }

  async function getTaxCode(taxCodeId: string): Promise<TaxCode> {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/tax/taxCodes/${taxCodeId}`,
      headers: { 'X-Tenant': tenant },
    };

    return toTax(await handleRequest<TaxCodeParsers.TaxCodeJson>(config));
  }

  async function getTaxJurisdiction(taxJurisdictionId: string) {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/tax/taxJurisdictions/${taxJurisdictionId}`,
      headers: { 'X-Tenant': tenant },
    };

    return toTaxJurisdictionRow(await handleRequest<TaxCodeParsers.TaxJurisdictionRowJson>(config));
  }

  async function createTaxLocation(taxLocationLink: { locationId: number; taxCodeId: string; shippingType: string }) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/tax/taxLocations`,
      headers: { 'X-Tenant': tenant },
      data: taxLocationLink,
    };

    return await handleRequest<TaxLocationLink>(config);
  }

  async function listTaxLocations(request: { locationId?: number; id?: string; taxCodeId?: string }) {
    const config = {
      method: 'get',
      url: `${contextPath}quipli-service/v1/tax/taxLocations`,
      queryParams: request,
      headers: { 'X-Tenant': tenant },
    };

    return await handleRequest<TaxLocationLink[]>(config);
  }

  async function searchTaxCategories(request: GenericSearchRequest<TaxCategory>): Promise<TaxCategory[]> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/tax/taxCategories/search`,
      headers: { 'X-Tenant': tenant },
      data: request,
    };
    const result = await handleRequest<TaxCodeParsers.TaxCategoryJson[]>(config);
    return result.map(TaxCodeParsers.toTaxCategory);
  }

  async function createTaxCategory(taxCategory: { name: string; description?: string }): Promise<TaxCategory> {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/tax/taxCategories`,
      data: taxCategory,
      headers: { 'X-Tenant': tenant },
    };

    return TaxCodeParsers.toTaxCategory(await handleRequest<TaxCodeParsers.TaxCategoryJson>(config));
  }

  async function updateTaxCategory(taxCategoryId: string, req: { name: string; description?: string }) {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/tax/taxCategories/${taxCategoryId}`,
      data: req,
      headers: { 'X-Tenant': tenant },
    };

    return TaxCodeParsers.toTaxCategory(await handleRequest<TaxCodeParsers.TaxCategoryJson>(config));
  }

  async function assignTaxCategoryToJurisdiction(taxJurisdictionCategory: {
    taxJurisdictionId: string;
    taxCategoryId: string;
  }) {
    const config = {
      method: 'put',
      url: `${contextPath}quipli-service/v1/tax/taxJurisdiction/${taxJurisdictionCategory.taxJurisdictionId}/category/${taxJurisdictionCategory.taxCategoryId}`,
      data: {
        taxJurisdictionCategory,
      },
      headers: { 'X-Tenant': tenant },
    };

    return await handleRequest<{ id: string }>(config);
  }

  async function assignTaxCategoryToItem(taxCategory: {
    itemId?: string;
    itemType: ItemType;
    taxCategoryId?: string;
    transactionType?: OrderType;
  }) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/tax/taxCategories/assignItem`,
      data: taxCategory,
      headers: { 'X-Tenant': tenant },
    };

    return await handleRequest<ItemTaxCategory | undefined>(config);
  }
  async function getTaxCategoryForItem(request: { itemType: ItemType; itemId?: string; transactionType?: OrderType }) {
    const config = {
      method: 'post',
      url: `${contextPath}quipli-service/v1/tax/taxCategories/getItemTaxCategory`,
      data: request,
      headers: { 'X-Tenant': tenant },
    };

    const json = await handleRequest<ItemTaxCategoryJson | undefined>(config);
    return json && TaxCodeParsers.toItemTaxCategory(json);
  }

  async function deleteTaxCode(taxCodeId: string): Promise<void> {
    const config = {
      method: 'delete',
      url: `${contextPath}quipli-service/v1/tax/taxCodes/${taxCodeId}`,
      headers: { 'X-Tenant': tenant },
    };

    return await handleRequest(config);
  }

  return {
    createTaxCode,
    createTaxJurisdiction,
    searchTaxJurisdictions,
    searchTaxCodes,
    getTaxCode,
    getTaxJurisdiction,
    createTaxLocation,
    getTaxCategoryForItem,
    listTaxLocations,
    updateTaxJurisdiction,
    updateTaxCode,
    searchTaxCategories,
    createTaxCategory,
    updateTaxCategory,
    assignTaxCategoryToJurisdiction,
    assignTaxCategoryToItem,
    deleteTaxCode,
  };
};
